import type { Direction } from "dockview-core"
import type { OPDockviewPanelDefinition, PanelDimensionLimitOptions, OPLayoutPanel, OPLayoutTemplate, OPLayoutTemplateDefaults } from "./types"

interface PanelPlacementReference {
  panelId: string
  direction: Direction
}

type PanelPlacementConfig = PanelDimensionLimitOptions & {
  panelId: string
  reference?: PanelPlacementReference
  renderer?: OPDockviewPanelDefinition["renderer"]
  inactive?: boolean
}

interface TemplateConfig {
  id: string
  placements: readonly PanelPlacementConfig[] | ((panels: readonly OPLayoutPanel[]) => readonly PanelPlacementConfig[])
  defaults?: Partial<OPLayoutTemplateDefaults>
  maxPanels?: number
  // When true, additional panels beyond the declared placements are added as tabs to the first placement.
  stack?: boolean
}

const baseTemplateDefaults: OPLayoutTemplateDefaults = {
  renderer: "onlyWhenVisible",
}

/**
 * Couples a template-level placement with a concrete layout panel (content). This is the "what and where" part of the template.
 * placement: describes where and how the panel should be positioned relative to others
 * (e.g., reference, direction, initial dimensions, optional renderer override).
 * panel: the actual component instance provided by the caller with a stable `panelId` and renderer.
 */
interface PanelPlacementPair {
  placement: PanelPlacementConfig
  panel: OPLayoutPanel
}

/**
 * Returns the placements if the panel defition is passed as a function otherwise returns the placements. This is the "when and where" part of the template.
 */
function getPlacements(
  placements: TemplateConfig["placements"],
  panels: readonly OPLayoutPanel[],
): readonly PanelPlacementConfig[] {
  return typeof placements === "function" ? placements(panels) : placements
}

/**
 * Pairs incoming layout panels with template placements and separates any overflow. If stackOverflow is true it will pair all panels with the placements. Otherwise it will ignore them.
 */
function getMainLayoutPanels(
  layoutPanels: readonly OPLayoutPanel[],
  placementPlan: readonly PanelPlacementConfig[],
  stackOverflow: boolean,
): { mainPanels: PanelPlacementPair[], overflowPanels: readonly OPLayoutPanel[] } {
  if (!stackOverflow) {
    const pairs = layoutPanels
      .slice(0, placementPlan.length)
      .map((panel, index) => ({ placement: placementPlan[index], panel }))
    return { mainPanels: pairs, overflowPanels: [] }
  }

  const pairs: PanelPlacementPair[] = []
  const overflowPanels: OPLayoutPanel[] = []

  layoutPanels.forEach((panel, index) => {
    if (index < placementPlan.length) {
      pairs.push({ placement: placementPlan[index], panel })
    } else {
      overflowPanels.push(panel)
    }
  })

  return { mainPanels: pairs, overflowPanels }
}

/**
 * Converts paired placements into Dockview `AddPanelOptions` and records resolved ids for reference lookups.
 */
function getPanelDefinitions(
  pairs: readonly PanelPlacementPair[],
  defaults: OPLayoutTemplateDefaults,
): { definitions: OPDockviewPanelDefinition[], resolvedIds: Record<string, string> } {
  const resolvedIds: Record<string, string> = {}
  const definitions: OPDockviewPanelDefinition[] = []

  pairs.forEach(({ placement, panel }) => {
    let position: OPDockviewPanelDefinition["position"]

    if (placement.reference) {
      const anchorPanelId = resolvedIds[placement.reference.panelId]

      if (anchorPanelId) {
        position = {
          referencePanel: anchorPanelId,
          direction: placement.reference.direction,
        }
      }
    }

    const baseDefinition = {
      id: panel.panelId,
      component: panel.panelId,
      title: panel.component.title,
      renderer: placement.renderer ?? defaults.renderer,
      initialWidth: placement.initialWidth,
      initialHeight: placement.initialHeight,
      widthRange: placement.widthRange,
      heightRange: placement.heightRange,
    }

    if (panel.component.floating === true) {
      definitions.push({
        ...baseDefinition,
        floating: true,
      })
    } else {
      definitions.push({
        ...baseDefinition,
        position,
      })
    }

    if (!resolvedIds[placement.panelId]) {
      resolvedIds[placement.panelId] = panel.panelId
    }
  })

  return { definitions, resolvedIds }
}

/**
 * Attaches overflow panels as additional tabs on the anchor panel (first placement by default).
 */
function getOverflowedPanels(
  overflowPanels: readonly OPLayoutPanel[],
  anchorTemplateId: string | undefined,
  resolvedIds: Record<string, string>,
  defaults: OPLayoutTemplateDefaults,
): OPDockviewPanelDefinition[] {
  if (overflowPanels.length === 0) {
    return []
  }

  const anchorPanelId = anchorTemplateId ? resolvedIds[anchorTemplateId] : undefined

  return overflowPanels.map(panel => {
    const base = {
      id: panel.panelId,
      component: panel.panelId,
      title: panel.component.title,
      renderer: defaults.renderer,
    }
    if (panel.component.floating === true) {
      return {
        ...base,
        floating: true,
      }
    }
    return {
      ...base,
      position: anchorPanelId ? { referencePanel: anchorPanelId, direction: "within" } : undefined,
    }
  })
}

export function createTemplate(config: TemplateConfig): OPLayoutTemplate {
  const defaults: OPLayoutTemplateDefaults = {
    ...baseTemplateDefaults,
    ...config.defaults,
  }

  function compileTemplate(panels: readonly OPLayoutPanel[]): OPDockviewPanelDefinition[] {
    if (panels.length === 0) {
      return []
    }

    const placementPlan = getPlacements(config.placements, panels)
    const stackOverflow = config.stack ?? false
    const { mainPanels, overflowPanels } = getMainLayoutPanels(panels, placementPlan, stackOverflow)

    const { definitions, resolvedIds } = getPanelDefinitions(mainPanels, defaults)
    const stackAnchorId = stackOverflow ? placementPlan[0]?.panelId : undefined
    const overflowDefinitions = stackOverflow ? getOverflowedPanels(overflowPanels, stackAnchorId, resolvedIds, defaults) : []

    return [...definitions, ...overflowDefinitions]
  }

  return {
    id: config.id,
    defaults,
    maxPanels: config.stack ? undefined : config.maxPanels ?? (Array.isArray(config.placements) ? config.placements.length : undefined),
    compile: compileTemplate,
  }
}

// Six placement rows: two tab stacks (main + secondary) plus the split between them — not "four panels" in the UI sense.
const HIERARCHICAL_TAB_GROUP_PLACEMENTS: readonly PanelPlacementConfig[] = [
  { panelId: "main", initialHeight: 500, widthRange: { min: "10%" } },
  { panelId: "main-tab-2", reference: { panelId: "main", direction: "within" } },
  { panelId: "secondary", reference: { panelId: "main", direction: "right" }, initialWidth: 700, widthRange: { min: "10%" } },
  { panelId: "secondary-tab-2", reference: { panelId: "secondary", direction: "within" } },
  { panelId: "secondary-tab-3", reference: { panelId: "secondary", direction: "within" } },
  { panelId: "secondary-tab-4", reference: { panelId: "secondary", direction: "within" } },
]

const HIERARCHICAL_LEGACY_PLACEMENTS: readonly PanelPlacementConfig[] = [
  { panelId: "main", initialHeight: 500, widthRange: { min: "25%" } },
  { panelId: "secondary", reference: { panelId: "main", direction: "right" }, initialWidth: 550, initialHeight: 450, widthRange: { min: "25%" } },
  { panelId: "bottom-left", reference: { panelId: "main", direction: "below" }, initialHeight: 300, widthRange: { min: "25%" } },
  { panelId: "bottom-right", reference: { panelId: "secondary", direction: "below" }, initialWidth: 550, initialHeight: 350, widthRange: { min: "25%" } },
]

// Four content panels in two tab groups (left: Asset View + Graph, right: KPI + Factors + overflow).
// Uses HIERARCHICAL_TAB_GROUP_PLACEMENTS when there are at least four panels; otherwise legacy 2x2.
export const HIERARCHICAL = createTemplate({
  id: "HIERARCHICAL",
  placements: (panels): readonly PanelPlacementConfig[] =>
    panels.length >= 4 ? HIERARCHICAL_TAB_GROUP_PLACEMENTS : HIERARCHICAL_LEGACY_PLACEMENTS,
  stack: true,
})

export const TEMPLATES = {
  HIERARCHICAL,
}

export const DEFAULT_TEMPLATE = HIERARCHICAL
